import { useTranslation } from 'react-i18next'
import { localizedName, localizedNameWithEn } from '../utils/localizedName'
import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack,
  Textarea, Select, ActionIcon, Tooltip, TextInput,
  Center, Loader, NumberInput, SimpleGrid, Divider, Progress, Grid, Image, FileButton, Paper, Anchor,
  Alert, List, ScrollArea,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconPlus, IconPackage, IconAlertTriangle, IconTrash,
  IconArrowDown, IconArrowUp, IconAdjustments,
  IconHistory, IconEdit, IconBan, IconRefresh, IconCamera, IconCopy,
  IconArrowsExchange, IconBuildingWarehouse, IconFileSpreadsheet,
  IconCheck, IconAlertCircle, IconDownload,
} from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'
import { Capacitor } from '@capacitor/core'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'
import api, {
  getInventoryItems, getInventorySummary, getItemMovements,
  createInventoryItem, updateInventoryItem, deactivateInventoryItem,
  receiveStock, receiveStockBatch, issueStock, adjustStock, getNextInventorySku,
  getCategories, getSubCategories, getLocations, getVendors,
  getBrands, getModels, createBrand, createCategory, createSubCategory,
  uploadInventoryImage, getDepartments,
  returnStockItem, transferStock, getItemStockByLocation,
  getStockLocations, createStockLocation, updateStockLocation,
} from '../api/client'
import SmartDateInput from '../components/SmartDateInput'

const UNIT_OPTIONS = [
  { value: 'PCS', label: 'PCS — ชิ้น' },
  { value: 'BOX', label: 'BOX — กล่อง' },
  { value: 'ROLL', label: 'ROLL — ม้วน' },
  { value: 'PACK', label: 'PACK — แพ็ค' },
  { value: 'SET', label: 'SET — ชุด' },
  { value: 'MTR', label: 'MTR — เมตร' },
  { value: 'LTR', label: 'LTR — ลิตร' },
  { value: 'KG', label: 'KG — กิโลกรัม' },
  { value: 'BLOCK', label: 'BLOCK — ก้อน' },
]

const KIND_COLOR: Record<string, string> = {
  RECEIVE: 'teal', ISSUE_DIRECT: 'blue', ISSUE_REPAIR: 'orange',
  ADJUST: 'gray', RETURN: 'lime',
}

const KIND_LABEL: Record<string, string> = {
  RECEIVE: 'Received', ISSUE_DIRECT: 'Issued', ISSUE_REPAIR: 'Used in Repair',
  ADJUST: 'Adjusted', RETURN: 'Returned',
}

const EMPTY_FORM = {
  sku: '', name: '', description: '',
  brand: '', model: '', category: '', subCategory: '',
  unit: 'PCS', itemType: 'BULK', minStock: 5, openingQty: 0,
  location: '', vendorId: null as number | null,
  purchasePrice: 0,
  poNumber: '', receivedDate: '', expiryDate: '',
  departmentCode: '',
}

// แยกออกมาเป็น component เพื่อให้ใช้ useState ได้ถูกต้องตาม Rules of Hooks (ห้ามใช้ใน .map() callback)
function LocationRow({ loc, isAdmin, onRefresh, t }: { loc: any; isAdmin: boolean; onRefresh: () => void; t: any }) {
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(loc.name)
  return (
    <Group justify="space-between" style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
      <Box style={{ flex: 1 }}>
        {editing ? (
          <Group gap="xs">
            <TextInput size="xs" value={editName} onChange={e => setEditName(e.target.value)} style={{ flex: 1 }} />
            <Button size="xs" color="teal" onClick={async () => {
              try { await updateStockLocation(loc.id, { name: editName }); setEditing(false); onRefresh(); } catch { }
            }}>{t('common.save')}</Button>
            <Button size="xs" variant="subtle" onClick={() => { setEditing(false); setEditName(loc.name) }}>{t('common.cancel')}</Button>
          </Group>
        ) : (
          <>
            <Text size="sm" fw={600}>{loc.name}</Text>
            <Text size="xs" c="dimmed">Code: {loc.code}</Text>
          </>
        )}
      </Box>
      <Group gap={6}>
        <Badge color={loc.isActive ? 'teal' : 'gray'} variant="light" size="sm">
          {loc.isActive ? t('common.active', 'ใช้งาน') : t('common.inactive', 'ปิด')}
        </Badge>
        {!editing && (
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => setEditing(true)}>
            <IconEdit size={14} />
          </ActionIcon>
        )}
        {isAdmin && !editing && loc.code !== 'MAIN' && (
          <ActionIcon size="sm" variant="subtle" color="red" onClick={async () => {
            if (!window.confirm(`ลบคลัง "${loc.name}" จริงไหม?`)) return
            try { await updateStockLocation(loc.id, { isActive: false }); onRefresh(); } catch { }
          }}>
            <IconBan size={14} />
          </ActionIcon>
        )}
      </Group>
    </Group>
  )
}

export default function InventoryPage() {
  const navigate = useNavigate()
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const { roles, departments: userDepts } = useAuth()
  const { t, i18n } = useTranslation()
  const isAdmin = roles.includes('ADMIN')
  const isAdminLevel = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')
  const userDept = !isAdminLevel ? (userDepts?.[0] ?? null) : null
  const [items, setItems]         = useState<any[]>([])
  const [summary, setSummary]     = useState<any>(null)
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [deptOptions, setDeptOptions] = useState<{ value: string; label: string }[]>([])
  const [movements, setMovements] = useState<any[]>([])
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false)
  const [formOpened, { open: openForm, close: closeForm }]         = useDisclosure(false)
  const [locModalOpened, { open: openLocModal, close: closeLocModal }] = useDisclosure(false)
  const [importOpened, { open: openImport, close: closeImport }]       = useDisclosure(false)
  const [editing, setEditing]     = useState<any>(null)
  const [form, setForm]           = useState({ ...EMPTY_FORM })
  const [saving, setSaving]       = useState(false)
  const [skuLoading, setSkuLoading] = useState(false)
  const [imageFile, setImageFile]   = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  // Master data for dropdowns
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([])
  const [subCategoryOptions, setSubCategoryOptions] = useState<{ value: string; label: string }[]>([])
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([])
  const [vendorOptions, setVendorOptions]     = useState<{ value: string; label: string }[]>([])
  const [brandOptions, setBrandOptions]       = useState<{ value: string; label: string }[]>([])
  const [modelOptions, setModelOptions]       = useState<{ value: string; label: string }[]>([])
  // คลังสต็อก (Stock Locations) - แยกจาก locationOptions ข้างบนซึ่งเป็นตำแหน่ง Asset/ห้องพัก
  const [stockLocations, setStockLocations] = useState<any[]>([])
  const [allModels, setAllModels]             = useState<any[]>([])
  const [allSubCats, setAllSubCats]           = useState<any[]>([])

  // ── Scan to Receive ─────────────────────────────────────────────────────
  const [scanModalOpen, { open: openScanModal, close: closeScanModal }] = useDisclosure(false)
  const [scanning, setScanning] = useState(false)
  const [scanNotFound, setScanNotFound] = useState<string | null>(null)
  const scanVideoRef = React.useRef<HTMLVideoElement | null>(null)
  const scanControlsRef = React.useRef<any>(null)
  const scanDecodedRef = React.useRef(false)

  // ── Batch Receive — รับหลายรายการพร้อมกัน ใช้ร่วมกันทั้งเพิ่มมือ (search) และสแกน ──
  const [batchCartOpen, { open: openBatchCart, close: closeBatchCart }] = useDisclosure(false)
  const [batchCart, setBatchCart] = useState<{ itemId: number; name: string; unit: string; qty: number; unitCost: number }[]>([])
  const [batchLocationId, setBatchLocationId] = useState<number | undefined>(undefined)
  const [batchNote, setBatchNote] = useState('')
  const [batchPicker, setBatchPicker] = useState<string | null>(null)
  const [batchSaving, setBatchSaving] = useState(false)

  const addToBatchCart = (item: any) => {
    setBatchCart(prev => {
      const existing = prev.find(r => r.itemId === item.id)
      if (existing) {
        return prev.map(r => r.itemId === item.id ? { ...r, qty: r.qty + 1 } : r)
      }
      return [...prev, { itemId: item.id, name: item.name, unit: item.unit, qty: 1, unitCost: item.unitCost ?? 0 }]
    })
  }

  const handleConfirmBatchReceive = async () => {
    if (batchCart.length === 0) return
    setBatchSaving(true)
    try {
      const res = await receiveStockBatch({
        locationId: batchLocationId,
        note: batchNote || undefined,
        items: batchCart.map(r => ({ itemId: r.itemId, qty: r.qty, unitCost: r.unitCost || undefined })),
      })
      const { successCount, failCount } = res.data
      if (failCount > 0) {
        notifications.show({ color: 'orange', message: t('inventory.batchPartialResult', '{{success}} received, {{fail}} failed', { success: successCount, fail: failCount }) })
      } else {
        notifications.show({ color: 'teal', message: t('inventory.batchReceivedAll', 'Received {{count}} item(s) successfully', { count: successCount }) })
      }
      setBatchCart([])
      setBatchNote('')
      closeBatchCart()
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('scan.genericError', 'Something went wrong') })
    } finally { setBatchSaving(false) }
  }

  // QuickAdd modal
  const [qaOpened, { open: openQA, close: closeQA }] = useDisclosure(false)
  const [qaType, setQAType] = useState<'brand'|'category'|'subcategory'>('brand')

  // ── Import ────────────────────────────────────────────────────────────────
  const [importFile, setImportFile]     = useState<File | null>(null)
  const [importing, setImporting]       = useState(false)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  // คอลัมน์ที่ใช้ทั้ง template และ export — ต้องตรงกันทุกคอลัมน์
  const INVENTORY_COLUMNS = [
    { key: 'sku',         label: 'sku' },
    { key: 'name',        label: 'name' },
    { key: 'category',    label: 'category' },
    { key: 'subCategory', label: 'subCategory' },
    { key: 'brand',       label: 'brand' },
    { key: 'model',       label: 'model' },
    { key: 'unit',        label: 'unit' },
    { key: 'stockQty',    label: 'stockQty' },
    { key: 'minStock',    label: 'minStock' },
    { key: 'unitCost',    label: 'unitCost' },
    { key: 'description', label: 'description' },
  ]

  const downloadImportTemplate = () => {
    const headers = INVENTORY_COLUMNS.map(c => c.label)
    const ex1 = ['', 'หลอดไฟ LED 9W', 'CONSUMABLE', '', 'PHILIPS', 'LED-9W', 'PCS', '100', '20', '45', 'หลอดไฟ LED ขนาด 9 วัตต์']
    const ex2 = ['', 'น้ำดื่ม 600ml', 'CONSUMABLE', '', '', '', 'PCS', '200', '50', '8', 'น้ำดื่มบรรจุขวด']
    const csv = [headers.join(','), ex1.join(','), ex2.join(',')].join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob); a.download = 'inventory_import_template.csv'; a.click()
  }

  const handleExportCSV = () => {
    if (!items.length) return
    const headers = INVENTORY_COLUMNS.map(c => c.label)
    const rows = items.map(item =>
      INVENTORY_COLUMNS.map(c => {
        const v = String((item as any)[c.key] ?? '')
        return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v
      }).join(',')
    )
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob); a.download = `inventory_export_${new Date().toISOString().slice(0,10)}.csv`; a.click()
  }

  const handleImport = async () => {
    if (!importFile) return
    setImporting(true)
    try {
      const form = new FormData()
      form.append('file', importFile)
      const res = await api.post('/inventory/items/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      setImportResult(res.data)
      if (res.data.imported > 0) load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
    } finally { setImporting(false) }
  }
  const [qaForm, setQAForm] = useState({ code: '', name: '', parentCategory: '' })
  const [qaSaving, setQASaving] = useState(false)

  const load = async () => {
    setLoading(true)
    try {
      const [its, sum, cats, subCats, locs, vends, brands, mods, depts] = await Promise.all([
        getInventoryItems(), getInventorySummary(),
        getCategories(), getSubCategories(), getLocations(), getVendors(), getBrands(), getModels(), getDepartments(),
      ])
      setItems(its.data)
      setSummary(sum.data)
      setCategoryOptions((cats.data as any[]).filter(c => c.isActive).map(c => ({ value: c.code, label: localizedName(c, i18n.language) })))
      setAllSubCats(subCats.data as any[])
      setSubCategoryOptions((subCats.data as any[]).filter(s => s.isActive !== false).map(s => ({ value: s.code, label: localizedName(s, i18n.language) })))
      setLocationOptions((locs.data as any[]).filter(l => l.isActive).map(l => ({ value: l.code, label: localizedName(l, i18n.language) })))
      setVendorOptions((vends.data as any[]).filter(v => v.isActive).map(v => ({ value: String(v.id), label: localizedName(v, i18n.language) })))
      setBrandOptions((brands.data as any[]).filter(b => b.isActive !== false).map(b => ({ value: b.code, label: localizedNameWithEn(b, i18n.language) })))
      setAllModels(mods.data as any[])
      setDeptOptions((depts.data as any[]).filter(d => d.isActive !== false).map(d => ({ value: d.code, label: d.name ?? d.code })))
    } finally { setLoading(false) }
    loadStockLocations()
  }

  const loadStockLocations = async () => {
    try {
      let res = await getStockLocations()
      if (res.data.length === 0) {
        // ยังไม่มีคลังเลย สร้างคลังหลัก (MAIN) ให้อัตโนมัติ เพื่อให้เลือกใน dropdown ได้ตั้งแต่ครั้งแรก
        await createStockLocation({ code: 'MAIN', name: 'คลังหลัก' }).catch(() => {})
        res = await getStockLocations()
      }
      setStockLocations(res.data)
    } catch { /* ไม่ critical ถ้าโหลดไม่สำเร็จ - ระบบยัง fallback ไปคลังหลักอัตโนมัติที่ backend ได้ */ }
  }

  const loadModels = async (brandCode?: string) => {
    const res = await getModels(brandCode)
    setAllModels(res.data as any[])
    setModelOptions((res.data as any[]).map(m => ({ value: m.code, label: m.name })))
  }

  const handleBrandChange = (val: string | null) => {
    setForm(p => ({ ...p, brand: val ?? '', model: '' }))
    setModelOptions([])
    setAllModels([])
    if (val) loadModels(val)
  }

  const handleCategoryChange = (val: string | null) => {
    // แนะนำหน่วยนับจากของที่มีอยู่แล้วในหมวดนี้ (หน่วยที่ใช้บ่อยสุด) — ลดคลิกไม่ต้องเลือกเอง
    // ถ้าหมวดนี้ยังไม่เคยมีของเลย ไม่แตะหน่วยเดิมที่เลือกไว้
    let suggestedUnit: string | undefined
    if (val) {
      const inCategory = items.filter((i: any) => i.category === val)
      if (inCategory.length > 0) {
        const counts: Record<string, number> = {}
        for (const i of inCategory) counts[i.unit] = (counts[i.unit] ?? 0) + 1
        suggestedUnit = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]
      }
    }
    setForm(p => ({ ...p, category: val ?? '', subCategory: '', ...(suggestedUnit ? { unit: suggestedUnit } : {}) }))
    const filtered = allSubCats.filter(s => s.category === val && s.isActive !== false)
    setSubCategoryOptions(filtered.map(s => ({ value: s.code, label: localizedName(s, i18n.language) })))
  }

  // QuickAdd handler
  const openQuickAdd = (type: typeof qaType, parentCat = '') => {
    setQAType(type); setQAForm({ code: '', name: '', parentCategory: parentCat }); openQA()
  }

  const handleQuickAdd = async () => {
    const code = qaForm.code.trim().toUpperCase()
    const name = qaForm.name.trim()
    if (!name) { notifications.show({ color: 'red', message: 'Name is required' }); return }
    if (!code) { notifications.show({ color: 'red', message: 'Code is required' }); return }
    setQASaving(true)
    try {
      if (qaType === 'brand') {
        await createBrand({ code, name })
        setBrandOptions(p => [...p, { value: code, label: name }])
        handleBrandChange(code)
      } else if (qaType === 'category') {
        await createCategory({ code, name })
        setCategoryOptions(p => [...p, { value: code, label: name }])
        handleCategoryChange(code)
      } else if (qaType === 'subcategory') {
        await createSubCategory({ code, name, category: qaForm.parentCategory })
        setAllSubCats(p => [...p, { code, name, category: qaForm.parentCategory, isActive: true }])
        setSubCategoryOptions(p => [...p, { value: code, label: name }])
        setForm(p => ({ ...p, subCategory: code }))
      }
      notifications.show({ color: 'teal', message: `Added: ${name}` })
      closeQA()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setQASaving(false) }
  }

  useEffect(() => { load() }, [])

  const filtered = items.filter(i => {
    const matchSearch = i.name.toLowerCase().includes(search.toLowerCase()) || i.sku.toLowerCase().includes(search.toLowerCase())
    const matchDept = !deptFilter || i.departmentCode === deptFilter
    return matchSearch && matchDept
  })

  const lowStockItems = items.filter(i => i.stockQty <= i.minStock && i.stockQty > 0)
  const outOfStock    = items.filter(i => i.stockQty === 0)

  const fetchNextSku = async () => {
    setSkuLoading(true)
    try {
      const res = await getNextInventorySku()
      setForm(p => ({ ...p, sku: res.data.sku }))
    } finally { setSkuLoading(false) }
  }

  // ── Open history modal ────────────────────────────────────────────────────
  const handleViewHistory = async (item: any) => {
    setSelectedItem(item)
    const res = await getItemMovements(item.id)
    setMovements(res.data)
    openHistory()
  }

  // ── Receive stock ─────────────────────────────────────────────────────────
  const handleReceive = (item: any) => {
    let qty = 1, cost = item.unitCost ?? 0, note = ''
    let locationId = stockLocations[0]?.id
    modals.openConfirmModal({
      title: `Receive Stock — ${item.name}`,
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Current stock (all locations): <b>{item.stockQty} {item.unit}</b></Text>
          {stockLocations.length > 1 && (
            <Select label={t('inventory.stockLocation','เข้าคลัง')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
              defaultValue={String(stockLocations[0]?.id)} onChange={(v) => { locationId = v ? +v : stockLocations[0]?.id }} />
          )}
          <NumberInput label="Quantity to receive *" min={1} defaultValue={1}
            onChange={(v) => { qty = Number(v) }} />
          <NumberInput label={t("inventory.labelUnitCost","Unit Cost (฿)")} min={0} decimalScale={2} defaultValue={cost}
            onChange={(v) => { cost = Number(v) }} />
          <Textarea label={t("common.note","Note")} placeholder={t("inventory.poSupplierPlaceholder","PO number, supplier, etc.")}
            onChange={(e) => { note = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('inventory.receive','Receive'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        await receiveStock(item.id, { qty, locationId, unitCost: cost || undefined, note: note || undefined })
        notifications.show({ color: 'teal', message: `Received ${qty} ${item.unit} of ${item.name}` })
        load()
      },
    })
  }

  // ── Scan to Receive: สแกน SKU/barcode ของสินค้าที่มีอยู่แล้ว → ข้ามฟอร์มไปหน้ารับของเลย ──
  const stopScan = () => {
    try {
      if (scanControlsRef.current) { scanControlsRef.current.stop(); scanControlsRef.current = null }
      BrowserMultiFormatReader.releaseAllStreams()
      if (scanVideoRef.current) scanVideoRef.current.srcObject = null
    } catch {}
    setScanning(false)
  }

  const handleScanResult = (code: string) => {
    const match = items.find((i: any) => i.sku?.toLowerCase() === code.trim().toLowerCase())
    if (match) {
      addToBatchCart(match)
      notifications.show({ color: 'teal', message: t('inventory.scanAdded', 'Added: {{name}} (scan more or tap Done)', { name: match.name }), autoClose: 1500 })
      // เว้นจังหวะสั้นๆ ก่อนให้สแกนต่อได้ กันไม่ให้บาร์โค้ดเดิมที่ยังอยู่ในเฟรมถูกนับซ้ำทันที
      setTimeout(() => { scanDecodedRef.current = false }, 1200)
    } else {
      setScanNotFound(code.trim())
      stopScan()
    }
  }

  const startScan = async () => {
    try {
      scanDecodedRef.current = false
      setScanNotFound(null)
      setScanning(true)
      const codeReader = new BrowserMultiFormatReader()
      const videoElement = scanVideoRef.current
      if (!videoElement) { setScanning(false); return }
      scanControlsRef.current = await codeReader.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoElement,
        (result, err) => {
          if (result && !scanDecodedRef.current) {
            scanDecodedRef.current = true
            handleScanResult(result.getText())
          }
          if (err && !(err instanceof NotFoundException)) { /* ignore continuous no-result */ }
        }
      )
    } catch {
      notifications.show({ color: 'red', message: t('inventory.cameraError', 'Could not access camera') })
      setScanning(false)
    }
  }

  useEffect(() => {
    if (!scanModalOpen) stopScan()
    return () => stopScan()
  }, [scanModalOpen])

  // ── Issue stock ───────────────────────────────────────────────────────────
  const handleIssue = (item: any) => {
    let qty = 1, note = '', kind = 'ISSUE_DIRECT'
    let locationId = stockLocations[0]?.id
    modals.openConfirmModal({
      title: `Issue Stock — ${item.name}`,
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Available (all locations): <b>{item.stockQty} {item.unit}</b></Text>
          {stockLocations.length > 1 && (
            <Select label={t('inventory.stockLocation','เบิกจากคลัง')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
              defaultValue={String(stockLocations[0]?.id)} onChange={(v) => { locationId = v ? +v : stockLocations[0]?.id }} />
          )}
          <Select label={t("common.issueType","Issue type")} defaultValue="ISSUE_DIRECT"
            data={[
              { value: 'ISSUE_DIRECT', label: 'Direct use' },
              { value: 'ISSUE_REPAIR', label: 'Used in repair / maintenance' },
            ]}
            onChange={(v) => { kind = v ?? 'ISSUE_DIRECT' }} />
          <NumberInput label="Quantity *" min={1} max={item.stockQty} defaultValue={1}
            onChange={(v) => { qty = Number(v) }} />
          <Textarea label={t("inventory.noteAssetCode","Note / Asset code")}
            placeholder="e.g. Used on IT-UPS-0001 maintenance"
            onChange={(e) => { note = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('inventory.issue','Issue'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        await issueStock(item.id, { qty, locationId, kind, note: note || undefined })
        notifications.show({ color: 'orange', message: `Issued ${qty} ${item.unit} of ${item.name}` })
        load()
      },
    })
  }

  // ── Adjust stock ──────────────────────────────────────────────────────────
  const handleAdjust = (item: any) => {
    let newQty = item.stockQty, note = ''
    let locationId = stockLocations[0]?.id
    modals.openConfirmModal({
      title: `Adjust Stock — ${item.name}`,
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Current (all locations): <b>{item.stockQty} {item.unit}</b></Text>
          {stockLocations.length > 1 && (
            <Select label={t('inventory.stockLocation','คลังที่จะปรับ')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
              defaultValue={String(stockLocations[0]?.id)} onChange={(v) => { locationId = v ? +v : stockLocations[0]?.id }} />
          )}
          <NumberInput label={t('inventory.newQtyAtLocation','New quantity at this location *')} min={0} defaultValue={item.stockQty}
            onChange={(v) => { newQty = Number(v) }} />
          <Textarea label={t("common.reason","Reason")} placeholder={t("inventory.stocktakeReasonPlaceholder","Stocktake result, damage, etc.")}
            onChange={(e) => { note = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('inventory.adjust','Adjust'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'gray' },
      onConfirm: async () => {
        await adjustStock(item.id, { newQty, locationId, note: note || undefined })
        notifications.show({ color: 'lime', message: `Stock adjusted at location` })
        load()
      },
    })
  }

  // ── Return stock (คืนของที่ยังไม่ได้ใช้) ────────────────────────────────
  const handleReturn = (item: any) => {
    let qty = 1, note = ''
    let locationId = stockLocations[0]?.id
    modals.openConfirmModal({
      title: `Return Stock — ${item.name}`,
      children: (
        <Stack gap="sm">
          {stockLocations.length > 1 && (
            <Select label={t('inventory.stockLocation','คืนเข้าคลัง')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
              defaultValue={String(stockLocations[0]?.id)} onChange={(v) => { locationId = v ? +v : stockLocations[0]?.id }} />
          )}
          <NumberInput label={t('inventory.qtyToReturn','Quantity to return *')} min={1} defaultValue={1}
            onChange={(v) => { qty = Number(v) }} />
          <Textarea label={t("common.note","Note")} placeholder={t('inventory.returnNotePlaceholder','เบิกเกินมา/ไม่ได้ใช้')}
            onChange={(e) => { note = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('inventory.returnBtn','Return'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'grape' },
      onConfirm: async () => {
        await returnStockItem(item.id, { qty, locationId, note: note || undefined })
        notifications.show({ color: 'grape', message: `Returned ${qty} ${item.unit} of ${item.name}` })
        load()
      },
    })
  }

  // ── Transfer stock ระหว่างคลัง ──────────────────────────────────────────
  const handleTransfer = (item: any) => {
    if (stockLocations.length < 2) {
      notifications.show({ color: 'orange', message: t('inventory.needTwoLocations','ต้องมีอย่างน้อย 2 คลัง ถึงจะย้ายของได้ - เพิ่มคลังใหม่ก่อน') })
      return
    }
    let qty = 1, note = ''
    let fromLocationId = stockLocations[0]?.id
    let toLocationId = stockLocations[1]?.id
    modals.openConfirmModal({
      title: `Transfer Stock — ${item.name}`,
      children: (
        <Stack gap="sm">
          <Select label={t('inventory.fromLocation','จากคลัง')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
            defaultValue={String(stockLocations[0]?.id)} onChange={(v) => { fromLocationId = v ? +v : stockLocations[0]?.id }} />
          <Select label={t('inventory.toLocation','ไปคลัง')} data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
            defaultValue={String(stockLocations[1]?.id)} onChange={(v) => { toLocationId = v ? +v : stockLocations[1]?.id }} />
          <NumberInput label="Quantity *" min={1} defaultValue={1}
            onChange={(v) => { qty = Number(v) }} />
          <Textarea label={t("common.note","Note")} onChange={(e) => { note = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('inventory.transferBtn','Transfer'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'indigo' },
      onConfirm: async () => {
        if (fromLocationId === toLocationId) {
          notifications.show({ color: 'red', message: t('inventory.sameLocationError','คลังต้นทาง/ปลายทางต้องไม่ใช่คลังเดียวกัน') })
          return
        }
        try {
          await transferStock(item.id, { fromLocationId, toLocationId, qty, note: note || undefined })
          notifications.show({ color: 'indigo', message: `Transferred ${qty} ${item.unit} of ${item.name}` })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
        }
      },
    })
  }

  // ── Manage stock locations (เพิ่มคลังใหม่) ───────────────────────────────
  const handleAddLocation = () => {
    let code = '', name = ''
    modals.openConfirmModal({
      title: t('inventory.addLocationTitle','เพิ่มคลังใหม่'),
      children: (
        <Stack gap="sm">
          <TextInput label={t('common.code','Code')} placeholder="e.g. HK-F3" onChange={(e) => { code = e.target.value }} />
          <TextInput label={t('common.name','Name')} placeholder="e.g. คลังแม่บ้านชั้น 3" onChange={(e) => { name = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('common.save','Save'), cancel: t('common.cancel','Cancel') },
      onConfirm: async () => {
        if (!code.trim() || !name.trim()) {
          notifications.show({ color: 'red', message: t('common.required') })
          return
        }
        try {
          await createStockLocation({ code: code.trim(), name: name.trim() })
          notifications.show({ color: 'teal', message: t('common.success') })
          loadStockLocations()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error') })
        }
      },
    })
  }

  // ── Create / Edit ─────────────────────────────────────────────────────────
  const handleOpenCreate = () => {
    setEditing(null)
    setForm({ ...EMPTY_FORM, departmentCode: userDept ?? '' })
    setModelOptions([])
    setImageFile(null)
    setImagePreview(null)
    openForm()
    setTimeout(async () => {
      setSkuLoading(true)
      try {
        const res = await getNextInventorySku()
        setForm(p => ({ ...p, sku: res.data.sku }))
      } finally { setSkuLoading(false) }
    }, 0)
  }

  const handleOpenEdit = (item: any) => {
    setEditing(item)
    setForm({
      sku: item.sku, name: item.name, description: item.description ?? '',
      brand: item.brand ?? '', model: item.model ?? '',
      category: item.category ?? '', subCategory: item.subCategory ?? '',
      unit: item.unit, itemType: item.itemType ?? 'BULK', minStock: item.minStock, openingQty: 0,
      location: item.location ?? '',
      poNumber: item.poNumber ?? '',
      receivedDate: item.receivedDate ? item.receivedDate.slice(0, 10) : '',
      expiryDate: item.expiryDate ? item.expiryDate.slice(0, 10) : '',
      vendorId: item.vendorId ?? null,
      purchasePrice: item.purchasePrice ?? 0,
      departmentCode: item.departmentCode ?? userDept ?? '',
    })
    // Load models for brand
    if (item.brand) loadModels(item.brand)
    // Load sub-categories for category
    if (item.category) {
      const filtered = allSubCats.filter(s => s.category === item.category && s.isActive !== false)
      setSubCategoryOptions(filtered.map(s => ({ value: s.code, label: localizedName(s, i18n.language) })))
    }
    setImageFile(null)
    setImagePreview(item.imageUrl ?? null)
    openForm()
  }

  const handleClone = async (item: any) => {
    setEditing(null)
    setForm({
      sku: '', name: `${item.name} (Copy)`,
      description: item.description ?? '',
      brand: item.brand ?? '', model: item.model ?? '',
      category: item.category ?? '', subCategory: item.subCategory ?? '',
      unit: item.unit, itemType: item.itemType ?? 'BULK', minStock: item.minStock, openingQty: 0,
      location: item.location ?? '',
      poNumber: item.poNumber ?? '',
      receivedDate: '',
      expiryDate: item.expiryDate ? item.expiryDate.slice(0, 10) : '',
      vendorId: item.vendorId ?? null,
      purchasePrice: item.purchasePrice ?? 0,
      departmentCode: item.departmentCode ?? userDept ?? '',
    })
    if (item.brand) loadModels(item.brand)
    if (item.category) {
      const filtered = allSubCats.filter(s => s.category === item.category && s.isActive !== false)
      setSubCategoryOptions(filtered.map(s => ({ value: s.code, label: localizedName(s, i18n.language) })))
    }
    setImageFile(null); setImagePreview(null)
    // auto-generate new SKU
    setSkuLoading(true)
    try {
      const res = await getNextInventorySku()
      setForm(p => ({ ...p, sku: res.data.sku }))
    } finally { setSkuLoading(false) }
    openForm()
  }

  const handleSave = async () => {
    if (!form.sku || !form.name) {
      notifications.show({ color: 'red', message: t('inventory.skuRequired','SKU and Name are required') }); return
    }
    setSaving(true)
    try {
      const { openingQty, unitCost: _u, ...rest } = form as any
      const payload = {
        ...rest,
        vendorId: form.vendorId ? Number(form.vendorId) : undefined,
        purchasePrice: form.purchasePrice != null ? Number(form.purchasePrice) : undefined,
        minStock: Number(form.minStock) || 0,
      }
      let savedId: number
      if (editing) {
        const res = await updateInventoryItem(editing.id, payload)
        savedId = res.data?.id ?? editing.id
        notifications.show({ color: 'teal', message: 'Item updated' })
      } else {
        const res = await createInventoryItem({ ...payload, openingQty })
        savedId = res.data?.id
        notifications.show({ color: 'teal', message: 'Item created' })
      }
      if (imageFile && savedId) {
        await uploadInventoryImage(savedId, imageFile).catch(() => {})
      }
      closeForm(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const f = (k: string) => (v: any) => setForm(p => ({ ...p, [k]: v }))

  // ── Stock level bar ───────────────────────────────────────────────────────
  const StockBar = ({ item }: { item: any }) => {
    const pct = item.minStock > 0 ? Math.min((item.stockQty / (item.minStock * 2)) * 100, 100) : 100
    const color = item.stockQty === 0 ? 'red' : item.stockQty <= item.minStock ? 'orange' : 'teal'
    return (
      <Box style={{ minWidth: 80 }}>
        <Group justify="space-between" mb={2}>
          <Text size="xs" fw={600} c={item.stockQty === 0 ? 'red' : item.stockQty <= item.minStock ? 'orange' : 'dark'}>
            {item.stockQty}
          </Text>
          <Text size="10px" c="dimmed">{item.unit}</Text>
        </Group>
        <Progress value={pct} color={color} size={3} radius="xl" />
      </Box>
    )
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px', color: '#0f172a' }}>
            {t('inventory.title','Inventory Consumable')}
          </Text>
          {userDept ? (
            <Text size="sm" c="dimmed">{t('inventory.showingAssetsFrom','Showing assets from')} <Badge color="teal" variant="light" size="sm" display="inline">{t('inventory.oneDepartment', '1 department: {{name}}', { name: deptOptions.find(d => d.value === userDept)?.label ?? userDept })}</Badge></Text>
          ) : (
            <Text size="sm" c="dimmed">{t('inventory.subtitle','Consumables and spare parts management')}</Text>
          )}
        </Box>
        <Group gap="xs">
          {/* แสดงจำนวนคลังทั้งหมด — จัดการได้ใน ตั้งค่าระบบ */}
          <Button variant="outline" color="gray" leftSection={<IconBuildingWarehouse size={15} />} onClick={openLocModal}>
            {t('inventory.manageLocations','คลัง')} ({stockLocations.length})
          </Button>
          {!Capacitor.isNativePlatform() && (
            <>
              <Button variant="outline" color="gray" leftSection={<IconFileSpreadsheet size={15} />}
                onClick={() => { setImportFile(null); setImportResult(null); openImport() }}>
                {t('inventory.importBtn','Import')}
              </Button>
              <Button variant="outline" color="gray" leftSection={<IconDownload size={15} />}
                onClick={handleExportCSV} disabled={!items.length}>
                {t('inventory.exportBtn','Export CSV')}
              </Button>
            </>
          )}
          <Button variant="outline" color="teal" leftSection={<IconCamera size={15} />}
            onClick={() => { setScanNotFound(null); openScanModal() }}>
            {t('inventory.scanToReceive', 'Scan to Receive')}
          </Button>
          <Button variant="outline" color="indigo" leftSection={<IconPackage size={15} />}
            onClick={() => { setBatchCart([]); setBatchNote(''); openBatchCart() }}>
            {t('inventory.batchReceive', 'Batch Receive')}
          </Button>
          <Button leftSection={<IconPlus size={15} />} onClick={handleOpenCreate}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
            {t("inventory.addItem","Add Item")}
          </Button>
        </Group>
      </Group>

      {/* Summary cards */}
      {summary && (
        <SimpleGrid cols={{ base: 2, md: 6 }} mb="xl">
          {[
            { label: t('inventory.totalItems','Total Items'), value: summary.totalItems, icon: IconPackage, color: '#0f172a' },
            { label: t('inventory.totalValue','Total Value'), value: `฿${Number(summary.totalValue).toLocaleString()}`, icon: IconPackage, color: '#0d9488' },
            { label: t('inventory.lowStock','Low Stock'), value: summary.lowStockCount, icon: IconAlertTriangle, color: '#f59e0b' },
            { label: t('inventory.outOfStock','Out of Stock'), value: summary.outOfStockCount, icon: IconAlertTriangle, color: '#ef4444' },
            { label: t('inventory.expiringSoon','Expiring Soon'), value: summary.expiringCount ?? 0, icon: IconAlertTriangle, color: '#f59e0b' },
            { label: t('inventory.expired','Expired'), value: summary.expiredCount ?? 0, icon: IconAlertTriangle, color: '#ef4444' },
          ].map(card => (
            <Box key={card.label} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
              <Text style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, letterSpacing: '0.3px', textTransform: 'uppercase', marginBottom: 6 }}>
                {card.label}
              </Text>
              <Text style={{ fontSize: 22, fontWeight: 600, color: card.color, letterSpacing: '-0.5px', fontFamily: 'Inter, sans-serif' }}>
                {card.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Main table */}
      <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        <Box style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{t("inventory.itemRegistry","Item Registry")}</Text>
          {lowStockItems.length > 0 && (
            <Badge color="orange" variant="light" size="sm">{lowStockItems.length} low stock</Badge>
          )}
          {outOfStock.length > 0 && (
            <Badge color="red" variant="light" size="sm">{outOfStock.length} out of stock</Badge>
          )}
          {isAdminLevel && (
            <Select size="xs" placeholder={t("inventory.allDepartmentsPlaceholder","All departments")} clearable searchable
              value={deptFilter} onChange={setDeptFilter}
              data={deptOptions} style={{ width: 180 }} />
          )}
          <TextInput
            placeholder={t("inventory.searchPlaceholder","Search by name or SKU...")}
            size="xs"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 220, marginLeft: 'auto' }}
            styles={{ input: { borderColor: '#e2e8f0', fontSize: 13 } }}
          />
        </Box>

        {loading ? (
          <Center p="xl"><Loader color="teal" size="sm" /></Center>
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8fafc' }}>
              <Table.Tr>
                <Table.Th style={{ width: 52 }}></Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colSKU','SKU')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colName','Name')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colCategory','Category')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colLocation','Location')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.currentStock','Stock')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colMin','Min')}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t("inventory.poNumber","PO Number")}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t("inventory.receive","Received")}</Table.Th>
                <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t('inventory.colCost','Unit Cost')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 ? (
                <Table.Tr><Table.Td colSpan={9}><Center p="xl"><Text c="dimmed" size="sm">{t('inventory.noItems','No items found')}</Text></Center></Table.Td></Table.Tr>
              ) : filtered.map(item => {
                // Unit Cost = purchasePrice / stockQty (ถ้าไม่มีให้ใช้ unitCost เดิม)
                const computedUnitCost = item.purchasePrice && item.stockQty > 0
                  ? Number(item.purchasePrice) / item.stockQty
                  : (item.unitCost ?? null)
                return (
                <Table.Tr key={item.id}>
                  {/* Photo column — model image > item image > placeholder */}
                  <Table.Td style={{ padding: '6px 8px' }}>
                    {(() => {
                      const modelImg = item.model
                        ? allModels.find((m: any) => m.code === item.model)?.imageUrl
                        : null
                      const src = modelImg ?? item.imageUrl ?? null
                      return src ? (
                        <Image src={src} w={40} h={40} radius="sm" fit="cover"
                          style={{ border: '1px solid #ebebeb', flexShrink: 0 }} />
                      ) : (
                        <Box style={{ width: 40, height: 40, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <IconPackage size={18} color="#cbd5e1" />
                        </Box>
                      )
                    })()}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600} style={{ fontFamily: 'Inter, sans-serif', color: '#0f172a' }}>{item.sku}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{item.name}</Text>
                    {item.vendorId && vendorOptions.find(v => v.value === String(item.vendorId)) && (
                      <Text size="xs" c="dimmed">{vendorOptions.find(v => v.value === String(item.vendorId))?.label}</Text>
                    )}
                    {item.itemType === 'PERISHABLE' && item.expiryDate && item.stockQty > 0 && (() => {
                      const days = Math.ceil((new Date(item.expiryDate).getTime() - Date.now()) / 86400000)
                      if (days < 0) return <Badge color="red" variant="light" size="xs" mt={2}>{t('inventory.expiredBadge','Expired')}</Badge>
                      if (days <= 30) return <Badge color="orange" variant="light" size="xs" mt={2}>{t('inventory.expiresInDays','Expires in {{count}}d', { count: days })}</Badge>
                      return null
                    })()}
                  </Table.Td>
                  <Table.Td>
                    {item.category ? <Badge variant="light" color="blue" size="sm">{item.category}</Badge> : <Text size="xs" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {locationOptions.find(l => l.value === item.location)?.label || item.location || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td><StockBar item={item} /></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{item.minStock}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{item.poNumber || '—'}</Text></Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {item.receivedDate ? new Date(item.receivedDate).toLocaleDateString('en-GB') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {computedUnitCost != null ? (
                      <Box>
                        <Text size="sm" fw={500}>฿{Number(computedUnitCost).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
                        {item.purchasePrice && item.stockQty > 0 && (
                          <Text size="10px" c="dimmed">฿{Number(item.purchasePrice).toLocaleString()} ÷ {item.stockQty}</Text>
                        )}
                      </Box>
                    ) : <Text size="sm" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label={t("inventory.receive","Receive stock")}>
                        <ActionIcon variant="subtle" color="teal" size="sm" onClick={() => handleReceive(item)}>
                          <IconArrowDown size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("inventory.issueStock","Issue stock")}>
                        <ActionIcon variant="subtle" color="orange" size="sm" onClick={() => handleIssue(item)}>
                          <IconArrowUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("inventory.adjust","Adjust")}>
                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => handleAdjust(item)}>
                          <IconAdjustments size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("inventory.transferBtn","Transfer")}>
                        <ActionIcon variant="subtle" color="indigo" size="sm" onClick={() => handleTransfer(item)}>
                          <IconArrowsExchange size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.history","History")}>
                        <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => handleViewHistory(item)}>
                          <IconHistory size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("inventory.cloneItem","Clone item")}>
                        <ActionIcon variant="subtle" color="blue" size="sm" onClick={() => handleClone(item)}>
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.edit","Edit")}>
                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => handleOpenEdit(item)}>
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.deactivate","Deactivate")}>
                        <ActionIcon variant="subtle" color="red" size="sm"
                          onClick={() => modals.openConfirmModal({
                            title: t('inventory.deactivateItemTitle', 'Deactivate item'),
                            children: <Text size="sm">{t('inventory.deactivateItemBody', 'Deactivate {{name}}? It will be hidden from the list.', { name: item.name })}</Text>,
                            labels: { confirm: t('common.deactivate', 'Deactivate'), cancel: t('common.cancel', 'Cancel') },
                            confirmProps: { color: 'red' },
                            onConfirm: async () => { await deactivateInventoryItem(item.id); load() },
                          })}>
                          <IconBan size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                )
              })}
            </Table.Tbody></Table>
        )}
      </Box>

      {/* History Modal */}
      <Modal opened={historyOpened} onClose={closeHistory} size="lg" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>{t('inventory.stockHistory','Stock History')} — {selectedItem?.name}</Text>}>
        <Table>
          <Table.Thead style={{ background: '#f8fafc' }}>
            <Table.Tr>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('common.date','Date')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('common.type','Type')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('inventory.qty','Qty')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('inventory.balance','Balance')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('common.by','By')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('inventory.usedFor','Used For')}</Table.Th>
              <Table.Th style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{t('common.note','Note')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {movements.length === 0 ? (
              <Table.Tr><Table.Td colSpan={7}><Center p="md"><Text size="sm" c="dimmed">t('inventory.noMovements','No movements yet')</Text></Center></Table.Td></Table.Tr>
            ) : movements.map(m => (
              <Table.Tr key={m.id}>
                <Table.Td><Text size="xs" c="dimmed">{new Date(m.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Text></Table.Td>
                <Table.Td><Badge size="xs" color={KIND_COLOR[m.kind] ?? 'gray'} variant="light">{KIND_LABEL[m.kind] ?? m.kind}</Badge></Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600} c={m.qty > 0 ? 'teal' : 'orange'}>
                    {m.qty > 0 ? `+${m.qty}` : m.qty}
                  </Text>
                </Table.Td>
                <Table.Td><Text size="sm">{m.balanceAfter}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{m.performedBy}</Text></Table.Td>
                <Table.Td>
                  {m.kind === 'ISSUE_REPAIR' && m.assetCode ? (
                    <Anchor size="xs" c="orange" fw={500}
                      onClick={() => { closeHistory(); navigate(`/maintenance?assetCode=${m.assetCode}`) }}>
                      🔧 {m.assetCode}{m.maintenanceId ? ` (Job #${m.maintenanceId})` : ''}
                    </Anchor>
                  ) : (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td><Text size="xs" c="dimmed" lineClamp={1}>{m.note || '—'}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal opened={formOpened} onClose={closeForm} size="lg" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>{editing ? t('inventory.editItem','Edit Consumable Item') : t('inventory.addItem','Add Consumable Item')}</Text>}>
        <Stack gap="sm">
          {/* ── SKU + Name ── */}
          <SimpleGrid cols={2}>
            <TextInput
              label={t("inventory.labelSKU","SKU")}
              value={form.sku}
              readOnly={!editing}
              disabled={!!editing}
              placeholder={skuLoading ? 'Generating...' : 'IT-CONS-XXXX'}
              rightSection={
                !editing ? (
                  <ActionIcon variant="subtle" color="gray" size="sm" loading={skuLoading} onClick={fetchNextSku}>
                    <IconRefresh size={13} />
                  </ActionIcon>
                ) : null
              }
              styles={{ input: { background: editing ? undefined : '#f8fafc', color: '#0f172a', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.3px' } }}
            />
            <TextInput label={t("inventory.labelName","Name *")} placeholder={t("inventory.namePlaceholder","e.g. AA Battery 9V")} value={form.name}
              onChange={e => f('name')(e.target.value)} />
          </SimpleGrid>

          {/* ── Department ── */}
          {isAdminLevel ? (
            <Select
              label={t('inventory.department','Department')}
              placeholder={t('config.selectDept','Select department...')}
              data={deptOptions}
              value={form.departmentCode || null}
              onChange={v => f('departmentCode')(v ?? '')}
              searchable clearable
              description={t('inventory.deptHint','Leave blank for shared / global item')}
            />
          ) : userDept ? (
            <TextInput
              label={t('inventory.department','Department')}
              value={deptOptions.find(d => d.value === userDept)?.label ?? userDept}
              readOnly
              styles={{ input: { background: '#f8fafc', fontWeight: 600 } }}
              description={t('inventory.deptAutoHint','Auto-assigned to your department')}
            />
          ) : null}

          {/* ── Model Image (read-only) ── */}
          {(() => {
            const selectedModel = allModels.find((m: any) => m.code === form.model)
            return selectedModel?.imageUrl ? (
              <Box p={12} style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <Text size="xs" fw={600} c="dimmed" mb={8} style={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>{t("inventory.modelImage","Model Image")}</Text>
                <Group gap="md" align="center">
                  <Image src={selectedModel.imageUrl} w={80} h={80} fit="cover" radius="md" style={{ border: '1px solid #ebebeb' }} />
                  <Box>
                    <Text size="sm" fw={600}>{selectedModel.name}</Text>
                    <Text size="xs" c="dimmed">{selectedModel.code}</Text>
                  </Box>
                </Group>
              </Box>
            ) : null
          })()}

          {/* ── Brand + Model ── */}
          <Grid gutter="sm">
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select label={t("inventory.brand","Brand")} placeholder={t("inventory.selectBrand","Select brand")} data={brandOptions}
                    value={form.brand || null} onChange={handleBrandChange} searchable clearable />
                </Box>
                <Tooltip label={t("inventory.addNewBrand","Add new brand")}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={() => openQuickAdd('brand')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Select label={t("inventory.model","Model")} placeholder={form.brand ? t('inventory.selectModel','Select model') : t('inventory.selectBrandFirst','Select brand first')}
                data={modelOptions} value={form.model || null} onChange={v => f('model')(v ?? '')}
                disabled={!form.brand} searchable clearable />
            </Grid.Col>
          </Grid>

          {/* ── Item Type — คุมว่าฟอร์มต้องมี Min Stock / Expiry Date ไหม ── */}
          <Select
            label={t("inventory.itemType", "Stock Tracking Type")}
            description={
              form.itemType === 'SPARE_PART'
                ? t("inventory.itemTypeSparePartHint", "Ad-hoc spare part — no reorder alert needed")
                : form.itemType === 'PERISHABLE'
                ? t("inventory.itemTypePerishableHint", "Tracks an expiry date — food, drinks, water, etc.")
                : t("inventory.itemTypeBulkHint", "Regularly restocked consumable — set a reorder alert below")
            }
            data={[
              { value: 'BULK', label: t("inventory.itemTypeBulk", "Bulk Consumable") },
              { value: 'SPARE_PART', label: t("inventory.itemTypeSparePart", "Spare Part (ad-hoc)") },
              { value: 'PERISHABLE', label: t("inventory.itemTypePerishable", "Perishable (has expiry date)") },
            ]}
            value={form.itemType}
            onChange={(v) => setForm(p => ({ ...p, itemType: (v ?? 'BULK') as any, minStock: v === 'SPARE_PART' ? 0 : p.minStock }))}
          />

          {form.itemType === 'PERISHABLE' && (
            <SmartDateInput
              label={t("inventory.expiryDate", "Expiry Date")}
              placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              description={t("inventory.expiryDateHint", "Update this each time a new batch is received")}
              value={form.expiryDate}
              onChange={(v) => setForm(p => ({ ...p, expiryDate: v }))}
            />
          )}

          {/* ── Category + Sub Category ── */}
          <Grid gutter="sm">
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select label={t("inventory.category","Category *")} placeholder={t("inventory.selectCategory","Select category")} data={categoryOptions}
                    value={form.category || null} onChange={handleCategoryChange} searchable clearable />
                </Box>
                <Tooltip label={t("inventory.addNewCategory","Add new category")}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} onClick={() => openQuickAdd('category')}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <Group gap={4} align="flex-end">
                <Box style={{ flex: 1 }}>
                  <Select label={t("inventory.subCategory","Sub Category")} placeholder={form.category ? t('inventory.selectSubCat','Select sub category') : t('inventory.selectCatFirst','Select category first')}
                    data={subCategoryOptions} value={form.subCategory || null}
                    onChange={v => f('subCategory')(v ?? '')} disabled={!form.category} searchable clearable />
                </Box>
                <Tooltip label={t("inventory.addNewSubCategory","Add new sub category")}>
                  <ActionIcon variant="light" color="teal" size="lg" mb={1} disabled={!form.category}
                    onClick={() => openQuickAdd('subcategory', form.category)}>
                    <IconPlus size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
          </Grid>

          {/* ── Quantity + Unit ── */}
          <SimpleGrid cols={2}>
            {!editing ? (
              <NumberInput label={t("inventory.quantity","Quantity")} min={0} value={form.openingQty} onChange={f('openingQty')} />
            ) : form.itemType !== 'SPARE_PART' ? (
              <NumberInput label={t("inventory.minStockAlert","Min Stock Alert")} min={0} value={form.minStock} onChange={f('minStock')} />
            ) : <Box />}
            <Select label={t("inventory.unit","Unit")} data={UNIT_OPTIONS} value={form.unit} onChange={f('unit')} />
          </SimpleGrid>

          {/* ── Min Stock (create) + Purchase Price ── */}
          <SimpleGrid cols={editing ? 1 : 2}>
            {!editing && form.itemType !== 'SPARE_PART' && (
              <NumberInput label={t("inventory.minStockAlert","Min Stock Alert")} min={0} value={form.minStock} onChange={f('minStock')} />
            )}
            <NumberInput label={t("inventory.purchasePrice","Purchase Price — Excl. VAT (฿)")} min={0} decimalScale={2} value={form.purchasePrice} onChange={f('purchasePrice')} />
          </SimpleGrid>

          {/* ── Location + Vendor ── */}
          <SimpleGrid cols={2}>
            <Select label={t("inventory.labelLocation","Storage Location")} placeholder={t("inventory.selectLocation","Select location...")} data={locationOptions}
              value={form.location || null} onChange={v => f('location')(v ?? '')} searchable clearable />
            <Select label={t("inventory.labelSupplier","Supplier")} placeholder={t("inventory.selectVendor","Select vendor...")} data={vendorOptions}
              value={form.vendorId ? String(form.vendorId) : null}
              onChange={v => f('vendorId')(v ? Number(v) : null)} searchable clearable />
          </SimpleGrid>

          {/* ── PO Number + Received Date ── */}
          <SimpleGrid cols={2}>
            <TextInput label={t("inventory.poNumber","PO Number")} placeholder={t("inventory.poPlaceholder","e.g. PO-2024-001")}
              value={form.poNumber} onChange={e => f('poNumber')(e.target.value)} />
            <SmartDateInput label={t("inventory.receivedDate","Received Date")} placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={form.receivedDate} onChange={v => f('receivedDate')(v)} clearable />
          </SimpleGrid>

          {/* ── Item Photo ── */}
          <Box>
            <Text size="sm" fw={500} mb={6}>{t("inventory.itemPhoto","Item Photo")}</Text>
            <Group gap={12} align="flex-start">
              {/* Preview */}
              <Box style={{
                width: 80, height: 80, borderRadius: 10,
                border: '1px dashed #cbd5e1', overflow: 'hidden',
                background: '#f8fafc', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {imagePreview ? (
                  <Image src={imagePreview} w={80} h={80} fit="cover" />
                ) : (
                  <IconCamera size={28} color="#cbd5e1" />
                )}
              </Box>
              <Stack gap={6} style={{ flex: 1 }}>
                <FileButton
                  onChange={(file) => {
                    if (!file) return
                    setImageFile(file)
                    setImagePreview(URL.createObjectURL(file))
                  }}
                  accept="image/*"
                >
                  {(props) => (
                    <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconCamera size={14} />}>
                      {imagePreview ? t('inventory.changePhoto','Change Photo') : t('inventory.uploadPhoto','Upload Photo')}
                    </Button>
                  )}
                </FileButton>
                {imageFile && (
                  <Text size="xs" c="dimmed">{imageFile.name} · {(imageFile.size / 1024).toFixed(0)} KB</Text>
                )}
                {imagePreview && !imageFile && (
                  <Text size="xs" c="dimmed">{t("inventory.currentPhoto","Current photo")}</Text>
                )}
              </Stack>
            </Group>
          </Box>

          <Textarea label={t("inventory.description","Description")} placeholder={t("inventory.descPlaceholder","Optional notes...")} value={form.description}
            onChange={e => f('description')(e.target.value)} />
          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeForm}>{t("common.cancel","Cancel")}</Button>
            <Button loading={saving} onClick={handleSave}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {editing ? t('inventory.saveChanges','Save Changes') : t('inventory.createItem','Create Item')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Scan to Receive Modal ── */}
      <Modal opened={scanModalOpen} onClose={closeScanModal} size="sm" radius="lg"
        transitionProps={{ onEntered: startScan, onExited: stopScan }}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>{t('inventory.scanToReceive', 'Scan to Receive')}</Text>}>
        <Stack gap="sm">
          {!scanNotFound ? (
            <>
              <Box style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
                <video ref={scanVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                <Box style={{
                  position: 'absolute', inset: '18%', border: '2px solid #6ee7b7', borderRadius: 12,
                  pointerEvents: 'none',
                }} />
              </Box>
              <Text size="xs" c="dimmed" ta="center">
                {t('inventory.scanHint', 'Scan the barcode on the item — must match an existing SKU')}
              </Text>
              {batchCart.length > 0 && (
                <Group justify="space-between" align="center" style={{ background: '#f0fdfa', borderRadius: 10, padding: '8px 12px' }}>
                  <Text size="sm" fw={600} c="teal">
                    {t('inventory.scannedCount', '{{count}} item(s) scanned', { count: batchCart.length })}
                  </Text>
                  <Button size="xs" color="teal" onClick={() => { closeScanModal(); stopScan(); openBatchCart() }}>
                    {t('inventory.doneScanning', 'Done — Review')}
                  </Button>
                </Group>
              )}
            </>
          ) : (
            <Stack align="center" gap="sm" py="md">
              <IconAlertCircle size={32} color="#f59e0b" />
              <Text size="sm" ta="center">
                {t('inventory.scanNoMatch', 'No item matches this code')}: <b>{scanNotFound}</b>
              </Text>
              <Text size="xs" c="dimmed" ta="center">
                {t('inventory.scanNoMatchHint', 'This item may not exist yet, or its SKU differs from the scanned code.')}
              </Text>
              <Group gap="sm">
                <Button variant="default" onClick={() => { setScanNotFound(null); startScan() }}>
                  {t('inventory.scanAgain', 'Scan Again')}
                </Button>
                <Button
                  style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}
                  onClick={() => { closeScanModal(); handleOpenCreate() }}>
                  {t('inventory.addAsNewItem', 'Add as New Item')}
                </Button>
              </Group>
              {batchCart.length > 0 && (
                <Button variant="light" color="teal" size="xs" onClick={() => { closeScanModal(); stopScan(); openBatchCart() }}>
                  {t('inventory.doneScanningCount', 'Done — Review {{count}} item(s)', { count: batchCart.length })}
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* ── Batch Receive Cart — รับหลายรายการพร้อมกัน (จากสแกนหรือเพิ่มมือ) ── */}
      <Modal opened={batchCartOpen} onClose={closeBatchCart} size="md" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>{t('inventory.batchReceiveTitle', 'Receive Multiple Items')}</Text>}>
        <Stack gap="sm">
          <Select
            label={t('inventory.addItemToBatch', 'Add item')}
            placeholder={t('inventory.searchItemPlaceholder', 'Search by name or SKU...')}
            searchable clearable
            data={items.map((i: any) => ({ value: String(i.id), label: `${i.name} (${i.sku})` }))}
            value={batchPicker}
            onChange={(v) => {
              setBatchPicker(null)
              if (v) { const item = items.find((i: any) => i.id === +v); if (item) addToBatchCart(item) }
            }}
          />
          {stockLocations.length > 1 && (
            <Select label={t('inventory.stockLocation', 'เข้าคลัง')}
              data={stockLocations.map(l => ({ value: String(l.id), label: l.name }))}
              value={batchLocationId ? String(batchLocationId) : String(stockLocations[0]?.id)}
              onChange={(v) => setBatchLocationId(v ? +v : undefined)} />
          )}
          {batchCart.length === 0 ? (
            <Center py="xl"><Text size="sm" c="dimmed">{t('inventory.batchCartEmpty', 'No items added yet')}</Text></Center>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('reports.item', 'Item')}</Table.Th>
                  <Table.Th w={90}>{t('common.qty', 'Qty')}</Table.Th>
                  <Table.Th w={100}>{t('inventory.labelUnitCost', 'Unit Cost (฿)')}</Table.Th>
                  <Table.Th w={40}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {batchCart.map((row, idx) => (
                  <Table.Tr key={row.itemId}>
                    <Table.Td><Text size="sm">{row.name}</Text><Text size="xs" c="dimmed">{row.unit}</Text></Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" min={1} value={row.qty}
                        onChange={(v) => setBatchCart(prev => prev.map((r, i) => i === idx ? { ...r, qty: Number(v) || 1 } : r))} />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" min={0} decimalScale={2} value={row.unitCost}
                        onChange={(v) => setBatchCart(prev => prev.map((r, i) => i === idx ? { ...r, unitCost: Number(v) || 0 } : r))} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon color="red" variant="subtle" size="sm" onClick={() => setBatchCart(prev => prev.filter((_, i) => i !== idx))}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <Textarea label={t('common.note', 'Note')} placeholder={t('inventory.poSupplierPlaceholder', 'PO number, supplier, etc.')}
            value={batchNote} onChange={(e) => setBatchNote(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeBatchCart}>{t('common.cancel', 'Cancel')}</Button>
            <Button loading={batchSaving} disabled={batchCart.length === 0} color="teal" onClick={handleConfirmBatchReceive}>
              {t('inventory.confirmReceiveAll', 'Receive All ({{count}})', { count: batchCart.length })}
            </Button>
          </Group>
        </Stack>
      </Modal>


      <Modal opened={qaOpened} onClose={closeQA} size="sm" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 15 }}>
          {qaType === 'brand' ? '+ Add Brand' : qaType === 'category' ? '+ Add Category' : '+ Add Sub Category'}
        </Text>}>
        <Stack gap="sm">
          <TextInput label="Code *"
            placeholder={qaType === 'brand' ? 'e.g. DELL, APPLE' : qaType === 'category' ? 'e.g. BATTERY, CABLE' : 'e.g. AA-BATTERY'}
            value={qaForm.code}
            onChange={e => setQAForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') }))}
            description="Uppercase, no spaces"
          />
          <TextInput label="Name *"
            placeholder={qaType === 'brand' ? 'e.g. Dell' : qaType === 'category' ? 'e.g. Battery' : 'e.g. AA Battery'}
            value={qaForm.name}
            onChange={e => setQAForm(p => ({ ...p, name: e.target.value }))}
          />
          {qaType === 'subcategory' && (
            <TextInput label={t("common.parentCategory","Parent Category")} value={qaForm.parentCategory} readOnly
              styles={{ input: { background: '#f8fafc' } }} />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={closeQA}>{t("common.cancel","Cancel")}</Button>
            <Button loading={qaSaving} onClick={handleQuickAdd}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>{t("common.add","Add")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Stock Locations Modal (view-only) ── */}
      <Modal opened={locModalOpened} onClose={closeLocModal} title={
        <Group gap="xs"><IconBuildingWarehouse size={20} /><Text fw={600} style={{ fontSize: 18 }}>{t('inventory.locationsTitle','คลังสต็อกทั้งหมด')}</Text></Group>
      } size="md" radius="lg">
        <Stack gap="md">
          <Box style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 14px', border: '1px solid #bfdbfe' }}>
            <Text size="xs" c="blue">💡 {t('inventory.locationsManageHint','จัดการคลัง (เพิ่ม/แก้ไข/ลบ) ได้ที่ ตั้งค่าระบบ → คลังสต็อก')}</Text>
          </Box>
          {stockLocations.length === 0 ? (
            <Text size="sm" c="dimmed">{t('inventory.noLocations','ยังไม่มีคลัง')}</Text>
          ) : (
            <Stack gap={6}>
              {stockLocations.map((loc: any) => (
                <Box key={loc.id} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0' }}>
                  <Group justify="space-between">
                    <Box>
                      <Text size="sm" fw={600}>{loc.name}</Text>
                      <Text size="xs" c="dimmed">{loc.code}</Text>
                    </Box>
                    <Badge color={loc.isActive ? 'teal' : 'gray'} variant="light" size="sm">
                      {loc.isActive ? t('common.active','ใช้งาน') : t('common.inactive','ปิดใช้')}
                    </Badge>
                  </Group>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Modal>

      {/* ── Import Inventory Modal ── */}
      <Modal opened={importOpened} onClose={() => { closeImport(); setImportFile(null); setImportResult(null) }}
        title={<Group gap="xs"><IconFileSpreadsheet size={20} /><Text fw={600} style={{ fontSize: 18 }}>{t('inventory.importTitle','Import รายการสินค้า')}</Text></Group>}
        size="md" radius="lg">
        <Stack gap="md">
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={600} mb={4}>{t('inventory.importRequiredFields','Required: name, unit')}</Text>
            <Text size="xs" c="dimmed">{t('inventory.importOptionalFields','Optional: sku (auto-generate), category, brand, stockQty, minStock, unitCost, location, description')}</Text>
          </Alert>
          <Button variant="subtle" size="xs" leftSection={<IconDownload size={14} />} onClick={downloadImportTemplate}>
            {t('inventory.downloadTemplate','Download CSV Template')}
          </Button>
          <Divider />
          <FileButton onChange={(f) => { setImportFile(f); setImportResult(null) }} accept=".csv,.xlsx,.xls">
            {(props) => (
              <Button {...props} variant="outline" leftSection={<IconFileSpreadsheet size={16} />} color="gray">
                {importFile ? importFile.name : t('inventory.chooseFile','Choose File (.csv / .xlsx)')}
              </Button>
            )}
          </FileButton>
          {importResult && (
            <Box>
              <Group gap="xl" mb="xs">
                <Box ta="center"><Text size="xl" fw={700} c="lime">{importResult.imported}</Text><Text size="xs" c="dimmed">{t('inventory.imported','นำเข้าแล้ว')}</Text></Box>
                <Box ta="center"><Text size="xl" fw={700} c="orange">{importResult.skipped}</Text><Text size="xs" c="dimmed">{t('inventory.skipped','ข้ามไป')}</Text></Box>
              </Group>
              {importResult.errors.length > 0 && (
                <ScrollArea h={120}>
                  <List size="xs" spacing={2}>
                    {importResult.errors.map((e, i) => <List.Item key={i} icon={<IconAlertCircle size={12} color="orange" />}>{e}</List.Item>)}
                  </List>
                </ScrollArea>
              )}
              {importResult.imported > 0 && (
                <Alert color="teal" variant="light" icon={<IconCheck size={16} />} mt="xs">
                  {t('inventory.importSuccess',`นำเข้า ${importResult.imported} รายการแล้ว`)}
                </Alert>
              )}
            </Box>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { closeImport(); setImportFile(null); setImportResult(null) }}>{t('common.close','ปิด')}</Button>
            <Button leftSection={<IconDownload size={16} />} disabled={!importFile || !!importResult}
              loading={importing} onClick={handleImport}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
              {t('inventory.importBtn','Import')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
